const berymoFaqData = [
  // ==================================================================
  // 1. DRIVER HELP Q&A - ALL VARIATIONS
  // ==================================================================
  
  // Order Tracking (generic)
  {
    keywords: [
      "track my order", "order tracking", "where is my order", "order status",
      "track order", "follow my order", "order live location"
    ],
    answer: `Open the **Orders** tab and select your order to see its live status. You’ll see when it’s accepted, prepared, and on the way. Turn on notifications to get updates in real time.`,
    quickReplies: ["How to contact support", "Cancel an order", "Payment methods"]
  },

  // Cancel an Order
  {
    keywords: [
      "cancel an order", "order cancel", "cancel my order", "how do i cancel my order",
      "how to cancel an order", "stop order", "cancel food order"
    ],
    answer: `You can cancel from the **Orders** tab: open the order and tap **Cancel Order**. If preparation has started, a fee may apply; if it’s already out for delivery, cancellation may not be available.`,
    quickReplies: ["Track my order", "How to contact support", "Payment methods"]
  },

  // Driver Account Creation
  {
    keywords: [
      "i'm a driver", "create driver account", "sign up driver app", "driver account", 
      "how do i create a driver account", "how do I create a driver account?",
      "driver signup", "become a driver", "driver registration", "register as driver",
      "create account driver", "new driver account", "driver sign up", "start as driver",
      "how to create driver account", "open driver account"
    ],
    answer: `Download the **Driver app** and tap **Sign Up**. Enter your mobile number or email, create a password, and complete verification steps. After verification, set up your profile and upload the required documents to start accepting rides.`,
    quickReplies: ["Required documents", "Go Online", "Earnings calculation"]
  },

  // Required Documents
  {
    keywords: [
      "required documents", "driver documents", "license", "registration", "insurance",
      "what documents do i need to provide", "what documents do I need to provide?",
      "documents needed driver", "upload documents", "document requirements",
      "driver license", "vehicle registration", "insurance driver", "inspection certificate",
      "verify documents", "document verification", "what docs needed", "which documents needed"
    ],
    answer: `You need a **driver's license**, **vehicle registration**, **inspection**, and **insurance**. Some cities may require additional documents. Ensure all documents are valid to avoid account suspension.`,
    quickReplies: ["Upload documents", "Account suspended", "Create driver account"]
  },

  // Go Online
  {
    keywords: [
      "go online", "accept rides", "driver online status", "how do i go online",
      "how do I go online and accept rides?", "go online driver",
      "start accepting rides", "start taking rides", "activate driver", "driver mode",
      "turn on driver", "enable driver", "online driver", "accept ride request",
      "receive ride requests", "tap accept"
    ],
    answer: `Tap the **Go Online** button. Once online, you will receive ride requests. Tap **Accept** to confirm the ride and navigate to the pickup location.`,
    quickReplies: ["Ride types I can drive", "Ride process driver", "Cancel a ride driver"]
  },

  // Ride Types Driver Can Drive
  {
    keywords: [
      "ride types i can drive", "ride types driver", "package ride driver", "flat ride driver",
      "place ride driver", "what ride types can i drive", "what ride types can I drive?",
      "ride types available", "delivery ride", "short distance ride", "free ride driver",
      "three ride types", "types of rides driver", "package delivery", "flat rate",
      "which rides can i take", "ride categories driver"
    ],
    answer: `Drivers can handle three types of rides: **Package Ride** (deliver packages or passengers with OTP verification), **Flat Ride** (short-distance fixed fare up to 8 miles), and **Place Ride** (free ride—rider completes Check-In at partnered place).`,
    quickReplies: ["How OTP works", "Handle Package Delivery", "Go Online"]
  },

  // Earnings Calculation
  {
    keywords: [
      "earnings calculation", "how are earnings calculated", "task rate", "bonuses",
      "how are earnings calculated?", "calculate earnings", "income calculation",
      "how much i earn", "earnings per ride", "ride payment", "payment calculation",
      "bonus system", "incentives", "earnings rate", "hourly rate", "per ride rate",
      "how income works", "payment breakdown"
    ],
    answer: `Earnings are based on completed rides, deliveries, or tasks. The system calculates income using **ride or task rates** multiplied by completed units. Bonuses may be added. Package deliveries or milestone tasks are paid after approval.`,
    quickReplies: ["Payment schedule", "View payout history", "Weekly Goal"]
  },

  // Payment Schedule
  {
    keywords: [
      "payment schedule", "when will i receive my payment", "weekly payment", "monthly payment",
      "when will I receive my payment?", "payment frequency", "payout schedule",
      "how often paid", "payment timing", "when do i get paid", "when do I get paid?",
      "deposit schedule", "payment day", "every monday", "monthly payout",
      "bank processing time", "holiday delays"
    ],
    answer: `Payments are processed **weekly** (every Monday) or **monthly** (on the 1st). You can switch schedules in settings. Delays may occur due to bank processing or holidays.`,
    quickReplies: ["How do I get paid?", "View payout history", "Payment delays"]
  },

  // View Payout History
  {
    keywords: [
      "view payout history", "payout history driver", "transaction history", "export csv",
      "how can i view my payout history", "how can I view my payout history?",
      "view payment history", "download payout", "transaction details", "earnings history",
      "payment records", "export payments", "csv export", "payout records",
      "view earnings", "transaction history"
    ],
    answer: `Go to **Payout History** in the menu. You can view dates, amounts, and statuses. **Export your history as a CSV** for record-keeping.`,
    quickReplies: ["Earnings calculation", "Payment schedule", "How do I get paid?"]
  },

  // How to Get Paid
  {
    keywords: [
      "how do i get paid", "get paid driver", "payout method", "card details",
      "how do I get paid?", "payment method", "add card", "bank account",
      "receive payment", "payout setup", "payment details", "card information",
      "account for payments", "update payment method", "change payout"
    ],
    answer: `Payments are credited based on your **payout method**. Add or update your card details to receive payouts. Earnings update after each completed ride.`,
    quickReplies: ["Change payout method", "Payment schedule", "View payout history"]
  },

  // Cancel Ride Driver
  {
    keywords: [
      "cancel a ride driver", "can i cancel a ride", "rider charged driver cancel",
      "can I cancel a ride?", "driver cancellation", "cancel ride",
      "cancellation policy driver", "rider not charged", "charge rider",
      "cancel ride refund", "cancel without charge", "no charge cancellation",
      "free cancellation"
    ],
    answer: `Yes. If the driver cancels, the **rider will not be charged** for the ride.`,
    quickReplies: ["Rider doesn't show up", "Cancellation policy", "Go Online"]
  },

  // Navigation
  {
    keywords: [
      "navigate driver app", "how do i navigate to the rider or delivery location",
      "how do I navigate to the rider or delivery location?", "in-app navigation",
      "gps directions", "turn by turn", "route navigation", "map navigation",
      "get directions", "navigation feature", "follow route", "turn directions",
      "gps not working", "navigation issues"
    ],
    answer: `Use the **in-app navigation feature** for turn-by-turn directions.`,
    quickReplies: ["GPS issues", "Report problem during ride", "Ride process driver"]
  },

  // Report Problem During Ride
  {
    keywords: [
      "report a problem driver", "how do i report a problem during a ride",
      "how do I report a problem during a ride?", "report problem during ride",
      "report issue", "support help driver", "contact support", "file complaint",
      "issue during ride", "accident report", "safety issue", "help during ride",
      "emergency", "problem during delivery"
    ],
    answer: `Go to **Help → Select Ride → Report Issue**. Support will review and respond.`,
    quickReplies: [ "Accident report", "Contact support"]
  },

  // Ride Process Driver
  {
    keywords: [
      "ride process driver", "how does the ride process work driver",
      "how does the ride process work?", "ride steps", "ride workflow",
      "complete a ride", "ride stages", "ride sequence", "step by step ride",
      "what happens during ride", "ride flow", "ride procedure", "ride completion"
    ],
    answer: `Go online → Accept ride → Navigate to pickup → **Verify OTP** → Complete ride → Confirm trip and collect payment if required.`,
    quickReplies: ["How OTP works", "Handle Package Delivery", "Cancel a ride driver"]
  },

  // Handle Package Deliveries
  {
    keywords: [
      "handle package deliveries", "package deliveries driver", "receiver otp", "delivery photo",
      "how do i handle package deliveries", "how do I handle package deliveries?",
      "deliver package", "package delivery steps", "collect package", "receiver details",
      "take photo delivery", "delivery confirmation", "package guidelines",
      "delivery process", "sealed package"
    ],
    answer: `Collect package details from the rider, deliver it to the receiver, enter their OTP, and upload a delivery photo. Packages must be **under 25 lbs** and properly sealed. No prohibited items allowed.`,
    quickReplies: ["Package guidelines", "How OTP works", "Ride process driver"]
  },

  // OTP Verification
  {
    keywords: [
      "how does otp verification work", "otp verification driver", "verify rider",
      "how does OTP verification work?", "otp verification process",
      "enter otp", "otp matching", "verification steps", "verify passenger",
      "otp not matching", "wrong otp", "otp verification", "receiver otp",
      "passenger verification", "otp code"
    ],
    answer: `Rider provides an **OTP**. Enter it in the app to verify and start the trip. For package deliveries, the **receiver** provides an OTP at delivery completion.`,
    quickReplies: ["OTP not working", "Ride process driver", "Handle Package Delivery"]
  },

  // Add Multiple Vehicles
  {
    keywords: [
      "add multiple vehicles", "can i add multiple vehicles to my account",
      "can I add multiple vehicles to my account?", "max vehicles", "active vehicle",
      "second vehicle", "add another car", "vehicle limit", "dual vehicles",
      "multiple cars driver", "add vehicle", "switch vehicles", "vehicle approval"
    ],
    answer: `Yes, up to **2 vehicles**. Only one can be active at a time. The second vehicle requires approval.`,
    quickReplies: ["Delete a vehicle", "Vehicle requirements", "Switch vehicle"]
  },

  // Delete Vehicle
  {
    keywords: [
      "delete a vehicle", "how can i delete a vehicle", "remove car",
      "how can I delete a vehicle?", "delete vehicle", "remove vehicle",
      "deactivate vehicle", "delete car", "vehicle deletion", "remove from app",
      "can't delete vehicle", "inactive vehicle"
    ],
    answer: `Go to **Vehicles → Select vehicle → Delete**. You can only delete a vehicle if you have 2 vehicles in your account, and the selected vehicle is not the active one.`,
    quickReplies: ["Add a new vehicle", "Active vehicle", "Vehicle requirements"]
  },

  // See Weekly Details
  {
    keywords: [
      "see weekly details", "what is 'see weekly details'",
      "what is see weekly details", "weekly summary", "weekly stats", "week overview",
      "earnings this week", "rides this week", "weekly breakdown", "weekly report",
      "tips earned", "bonus earned", "weekly tasks"
    ],
    answer: `**'See Weekly Details'** shows ride count, earnings, tips, bonuses, and completed tasks for the week.`,
    quickReplies: ["Weekly Goal", "Earnings calculation", "Payment schedule"]
  },

  // Weekly Goal
  {
    keywords: [
      "weekly goal", "what is a weekly goal", "earnings target",
      "what is a weekly goal?", "set weekly goal", "goal tracking", "income target",
      "weekly target", "earn more", "motivation goal", "target setting",
      "how to set goal", "weekly challenge"
    ],
    answer: `**Weekly Goal** allows drivers to set a target income for the week and track progress.`,
    quickReplies: ["See Weekly Details", "Bonuses", "Earnings calculation"]
  },

  // Rider Doesn't Show Up
  {
    keywords: [
      "rider doesn't show up", "what if a rider doesn't show up", "wait time",
      "what if a rider doesn't show up?", "cancel driver no show", "no show penalty",
      "rider not at pickup", "wait for rider", "rider late", "standing time",
      "cancellation fee no show", "rider missing"
    ],
    answer: `Wait for the recommended time. If the rider doesn't arrive, **cancel the ride**. A hard cancellation fee may apply based on circumstances.`,
    quickReplies: ["Cancellation fee details", "Report no-show", "Go Online"]
  },

  // Sign Out Driver
  {
    keywords: [
      "sign out driver", "how can i sign out from my driver account",
      "how can I sign out from my driver account?", "logout driver account",
      "driver logout", "log out", "sign off", "exit driver"
    ],
    answer: `Go to **Settings → Sign Out** to securely log out of your Driver account.`,
    quickReplies: ["Sign-in help", "Delete account driver", "Create driver account"]
  },

  // Delete Driver Account
  {
    keywords: [
      "delete account driver", "how can i delete my driver account",
      "how can I delete my driver account?", "remove driver account permanently",
      "delete driver account", "close account driver", "deactivate account",
      "permanently delete", "remove profile driver"
    ],
    answer: `Go to **Settings → Delete Account**. This permanently deletes your driver account.`,
    quickReplies: ["Sign out driver", "Contact support to delete", "Create driver account"]
  },

  // ==================================================================
  // 2. RIDER HELP Q&A - ALL VARIATIONS
  // ==================================================================

  // What is Berymo
  {
    keywords: [
      "what is berymo", "ride-hailing app", "features", "free ride",
      "what is berymo?", "berymo app", "berymo rides", "about berymo",
      "how berymo works", "berymo service", "berymo features", "berymo offer",
      "why berymo", "berymo vs other"
    ],
    answer: `**Berymo** is a ride-hailing app that helps you book rides quickly and safely. It offers **Package, Flat, and Place (Free Ride)** ride types, along with features like Check-In, vouchers, and secure payments for convenient and affordable travel.`,
    quickReplies: ["Create Rider account", "Book a ride", "What are the different types of rides?"]
  },

  // Create Rider Account
  {
    keywords: [
      "create rider account", "how do i create an account in the rider app",
      "how do I create an account in the rider app?", "sign up rider app",
      "verification rider", "rider signup", "new rider account", "register rider",
      "create account rider", "rider registration", "setup account", "download app"
    ],
    answer: `Download the **Rider app** and tap **Sign Up**. Enter your mobile number or email, complete verification via OTP or email confirmation, and your account will be ready for booking rides.`,
    quickReplies: ["Book a ride", "Forgot password", "Sign out Rider"]
  },

  // Book a Ride
  {
    keywords: [
      "book a ride", "how do i book a ride", "select destination",
      "how do I book a ride?", "request ride", "book ride steps", "ride booking",
      "start a ride", "where to search", "ride type selection", "fare estimation",
      "confirm booking", "book now", "get a ride"
    ],
    answer: `You can book a ride in two ways:
1. **Using Where To:** Enter destination, select ride type (**Package, Flat, or Place**), then **Request Ride**.
2. **Using Home Page Ride Types:** Tap Package, Flat, or Place directly, set destination, confirm and book.`,
    quickReplies: ["What are the different types of rides?", "Select destination", "Cancellation policy"]
  },

  // Select Destination
  {
    keywords: [
      "select destination rider", "how do i select my destination",
      "how do I select my destination?", "where to search", "home work address",
      "set destination", "destination options", "save address", "recent places",
      "map selection", "choose location", "pick location"
    ],
    answer: `You can enter your destination by typing in the **"Where To"** search bar, choosing directly on the map, selecting **Home or Work**, or picking from **Recent Places**.`,
    quickReplies: ["Book a ride", "Flat Ride Cost", "Navigation"]
  },

  // Ride Cost
  {
    keywords: [
      "ride cost", "how much does it cost to request a berymo ride",
      "how much does it cost to request a berymo ride?", "fare calculation",
      "flat ride cost", "package ride cost", "pricing", "how much charge",
      "fare estimate", "cost breakdown", "pricing details", "rate per mile",
      "distance charges", "time charges"
    ],
    answer: `We use simple pricing. **Flat Ride** and **Package Ride** have fixed fees up to 8 miles. After 8 miles, the fare increases based on your city's per-mile rate. **Place Ride** becomes free after completing Check-In and meeting the partner's minimum spending limit.`,
    quickReplies: ["See fare before booking", "Minimum spending limit", "Payment methods rider"]
  },

  // Ride Types - Main entry with detailed answer
  {
    keywords: [
      "ride types", "what are the different types of rides available in the app",
      "what are the different types of rides available in the app?", "what are the different rides",
      "what are the different types of rides", "different types of rides",
      "ride categories", "three rides", "package flat place", "which ride type", "ride options",
      "types of rides", "different rides"
    ],
    answer: `Our app offers three types of rides:

1. **Package Ride**
   • Ride based on a selected package with fixed distance/time.
   • Ideal for medium or long trips.
   • Extra distance/time will have additional charges.

2. **Flat Ride**
   • Fixed-price ride for short distances.
   • Simple pricing with no changes unless you cross the limit.

3. **Place (Free Ride)**
   • Free ride to our partnered places after check-in.
   • No charges—just select the partner location and book.`,
    quickReplies: ["What is Flat Ride", "What is Package Ride", "What is Place Ride", "Book a ride"]
  },

  // What is Flat Ride
  {
    keywords: [
      "what is flat ride", "what is a flat ride", "flat ride details", "what is a flat ride?",
      "flat ride pricing", "flat rate", "fixed price", "short distance ride",
      "8 miles", "flat ride cost", "flat ride how it works", "flat ride"
    ],
    answer: `**Flat Ride** is a short-distance ride option with a single fixed rate for trips up to 8 miles. It is ideal for quick, local trips where you want predictable pricing. After exceeding 8 miles, additional charges apply based on your city's per-mile rate.`,
    quickReplies: ["What is Package Ride", "What is Place Ride", "Ride cost", "Book a ride"]
  },

  // What is Package Ride - UPDATED with latest exact answer
  {
    keywords: [
      "what is a package ride", "what is package ride", "what is package ride?",
      "package ride details", "package ride?", "package ride pricing", "delivery ride", 
      "medium distance", "long distance ride", "package ride cost", "package ride how it works", 
      "with delivery", "package ride"
    ],
    answer: `A Package Ride gives you a fixed base fee for the first 8 miles. After that, the fare increases using your city’s per-mile rate. It’s great for medium or long-distance trips where you want simple, predictable pricing.`,
    quickReplies: ["What is Flat Ride", "What is Place Ride", "Package Delivery guidelines", "Ride cost", "Book a ride"]
  },

  // What is Place Ride
  {
    keywords: [
      "what is place ride", "what is place ride?", "place ride details", "place ride",
      "place (free ride) details", "free ride to restaurant", "place ride how it works"
    ],
    answer: `It’s a completely free ride to our partnered restaurants and places. The ride becomes free once you complete the Check-In and meet the partner’s minimum spend requirement.`,
    quickReplies: ["Check-In help", "Minimum spending limit", "Place (Free Ride)", "Book a ride"]
  },

  // Package Delivery Guidelines
  {
    keywords: [
      "package delivery guidelines", "package weight", "prohibited items",
      "package delivery guidelines?", "send package", "package restrictions",
      "package requirements", "package size", "25 lbs", "sealed package",
      "alcohol drugs weapons", "package insurance"
    ],
    answer: `Packages must be **under 25 lbs**, properly sealed, and must not contain prohibited items such as alcohol, drugs, or weapons. Berymo is not responsible for loss or damage.`,
    quickReplies: ["What is Package Ride", "Report damaged package", "Send package"]
  },

  // Place (Free Ride) - Kept for backward compatibility
  {
    keywords: [
      "place (free ride)", "what is a place (free ride)", "free ride details",
      "what is a place (free ride)?", "free ride concept", "partnered places",
      "check-in requirement", "free ride how", "restaurant free ride"
    ],
    answer: `A **free ride** to partnered restaurants and places. After **Check-In** and meeting the minimum spending requirement, the ride becomes completely free.`,
    quickReplies: ["Check-In help", "Minimum spending limit", "Book a ride"]
  },

  // Check-In Help
  {
    keywords: [
      "check-in help", "what does check-in mean", "confirm visit", "show bill qr",
      "what does check-in mean?", "check-in process", "check-in requirement",
      "check-in steps", "verify visit", "restaurant check-in", "qr code scan",
      "what is check-in", "check in", "check-in", "when check-in word show",
      "when come check-in word show that the", "check-in meaning",
      "how to check in", "check-in qr", "checkin", "check in help"
    ],
    answer: `**Check-In** means confirming your visit at our partnered place.
To get your Free Ride, you must:
• Arrive at the partnered restaurant/place
• Spend at least their minimum spending limit
• Show the bill/QR and complete the Check-In process in the app`,
    quickReplies: ["Minimum spending limit", "How to scan QR", "Where is the QR code?", "Get free ride after check-in"]
  },

  // Get Free Ride After Check-In
  {
    keywords: [
      "get free ride after check-in", "how do i get the free ride after check-in",
      "how do I get the free ride after check-in?", "scan qr free ride",
      "activate free ride", "complete check-in", "scan code", "claim free ride"
    ],
    answer: `Visit the partnered place, spend the minimum required amount, scan the **Check-In QR code**, and your free ride benefit will be activated automatically.`,
    quickReplies: ["Where is the QR code?", "Ride process rider", "Check-In help"]
  },

  // Minimum Spending Limit
  {
    keywords: [
      "minimum spending limit", "what is the minimum spending limit",
      "what is the minimum spending limit?", "qualify for free ride",
      "minimum amount", "spending requirement", "how much spend", "minimum spend"
    ],
    answer: `It is the **required amount** you must spend at the partnered place to receive the free ride. Each partner sets their own limit, visible in the app.`,
    quickReplies: [ "Place (Free Ride)", "Ride cost"]
  },

  // How to Show Proof Check-In
  {
    keywords: [
      "how do i show proof for check-in", "show proof for check-in",
      "how do I show proof for check-in?", "restaurant staff qr", "bill qr",
      "proof of visit", "verification", "scan qr code", "where is qr"
    ],
    answer: `After finishing your visit, the staff will provide a **Check-In QR code**. Scan it with your Rider app to activate your free ride.`,
    quickReplies: ["Check-In issues", "Where is the QR code?", "Get free ride after check-in"]
  },

  // What if Don't Meet Spending Limit
  {
    keywords: [
      "what happens if i don't meet the minimum spending limit",
      "what happens if I don't meet the minimum spending limit?", "fail to meet limit",
      "below minimum", "not enough spending", "short of minimum"
    ],
    answer: `You will **not qualify for the free ride**. Your ride will be charged normally.`,
    quickReplies: ["Minimum spending limit", "Place (Free Ride)", "Ride cost"]
  },

  // See Fare Before Booking
  {
    keywords: [
      "see fare before booking", "can i see the fare before booking",
      "can I see the fare before booking?", "estimated fare", "fare preview",
      "know price before", "transparent pricing", "see cost"
    ],
    answer: `**Yes**. The estimated fare is always shown before confirming your ride.`,
    quickReplies: ["Ride cost details", "Payment methods rider", "Book a ride"]
  },

  // Payment Methods Rider
  {
    keywords: [
      "payment issue", "what payment methods are available in the app",
      "what payment methods are available in the app?", "payment methods rider",
      "card", "bery cash", "apple pay", "payment option", "how to pay",
      "payment type", "add payment", "payment setup"
    ],
    answer: `You can pay using **Card** (Credit/Debit), **Berymo Cash**, or **Apple Pay**. Choose your preferred method before or after the ride.`,
    quickReplies: ["Payment issues", "Add new card", "Ride cost"]
  },

  // Ride Process Rider
  {
    keywords: [
      "ride process rider", "how does the ride process work rider",
      "how does the ride process work?", "otp verification", "ride steps",
      "complete ride", "ride workflow", "what happens during ride"
    ],
    answer: `Select your ride type and confirm. A driver gets assigned. Provide **OTP** to the driver for verification. After the ride is completed, rate and tip the driver.
• **Place Ride:** Complete Check-In afterward.
• **Package Delivery:** Receiver provides OTP; driver uploads delivery photo.`,
    quickReplies: ["Contact driver", "Cancel ride rider"]
  },

  // Cancel Ride Rider
  {
    keywords: [
      "cancel ride rider", "can i cancel my ride", "will i be charged rider",
      "can I cancel my ride?", "will I be charged?", "cancellation", "cancel request",
      "cancel before driver", "cancel after driver", "ride cancellation fee"
    ],
    answer: `Yes, you can cancel anytime. A **cancellation fee** may apply if the driver is already on the way or has arrived at your pickup location.`,
    quickReplies: ["Cancellation fee details", "Cancellation types", "Book a ride"]
  },

  // Cancellation Types
  {
    keywords: [
      "cancellation types", "what are the different types of ride cancellations",
      "what are the different types of ride cancellations?", "soft cancellation",
      "hard cancellation", "cancel before driver", "cancel after driver",
      "free cancellation", "charged cancellation", "cancellation options"
    ],
    answer: `There are two types:
1. **Soft Cancellation:** Before a driver is assigned. **No charge**.
2. **Hard Cancellation:** After a driver accepts. **Fee may apply**.`,
    quickReplies: ["Cancellation fee details", "Cancel ride rider", "Book a ride"]
  },

  // Contact Driver
  {
    keywords: [
      "contact driver", "how do i contact my driver", "call chat driver",
      "how do I contact my driver?", "call driver", "message driver", "driver communication",
      "reach driver", "talk to driver", "driver contact"
    ],
    answer: `After a driver accepts your ride, **Call** and **Chat** options will appear on your screen.`,
    quickReplies: ["Report a problem rider", "Ride process rider"]
  },

  // Report Problem Rider
  {
    keywords: [
      "report a problem rider", "how do i report a problem with my ride",
      "how do I report a problem with my ride?", "ride issue", "report issue",
      "file complaint", "contact support rider", "help", "problem during ride"
    ],
    answer: `Go to **Help**, select the ride, and choose the issue you want to report. Support will assist you.`,
    quickReplies: ["Submit a ticket", "Contact support"]
  },

  // Sign Out Rider
  {
    keywords: [
      "sign out rider", "how can i sign out from my rider account",
      "how can I sign out from my rider account?", "logout rider account",
      "log out rider", "sign off", "exit rider app"
    ],
    answer: `Go to **Settings → Sign Out** to safely log out of your Rider account.`,
    quickReplies: ["Sign-in help", "Delete account rider", "Create Rider account"]
  },

  // Delete Rider Account
  {
    keywords: [
      "delete account rider", "how can i remove rider", "how can I delete my rider account?",
      "remove rider account permanently", "delete rider account", "close account rider",
      "how do i delete my rider account?", "how can i delete my rider account?",
      "deactivate account rider", "remove profile"
    ],
    answer: `Go to **Settings** in the Rider app and click **Delete Account**. This will permanently delete your Rider account.`,
    quickReplies: ["Sign out rider", "Contact support to delete", "Create Rider account"]
  },

  // ==================================================================
  // 3. PLACES HELP Q&A - ALL VARIATIONS
  // ==================================================================

  // Create Account Places
  {
    keywords: [
      "restaurant partner", "how to create an account on the places website",
      "how to create an account on the places website?", "places sign up", "create account places",
      "registration places", "partner registration", "place signup", "open restaurant",
      "add my restaurant", "new restaurant account", "business signup"
    ],
    answer: `You can create an account by using the **Sign Up for Business** option on the Places website. Provide the required information such as first name, last name, and business details to complete the registration.`,
    quickReplies: ["Forgot Password", "What is the Dashboard?"]
  },

  // Sign-In Places
  {
    keywords: [
      "sign-in places", "how to sign-in to the places dashboard",
      "how to sign-in to the places dashboard?", "places owner login", "login dashboard",
      "signin dashboard", "restaurant login", "owner login", "place account login"
    ],
    answer: `Every place owner can sign in to the Places website using their created account with **email** and **password**.`,
    quickReplies: ["Forgot Password", "Dashboard overview", "Sign out places"]
  },

  // Forgot Password Places
  {
    keywords: [
      "forgot password places", "how does the forgot password feature work",
      "how does the forgot password feature work?", "reset password", "password reset",
      "forgot password", "lost password", "recover password", "change password"
    ],
    answer: `If you forget your password, click on the **Forgot Password** link on the login page. Enter your registered email address and you will receive a reset link to create a new password.`,
    quickReplies: ["Sign-in help", "Change password", ]
  },

  // What is Dashboard Places
  {
    keywords: [
      "what is the dashboard", "dashboard places", "new customer count", "service fees",
      "what is the dashboard?", "dashboard overview", "dashboard stats", "dashboard info",
      "customer metrics", "revenue dashboard", "business dashboard"
    ],
    answer: `The **Dashboard** displays new customer count, returning customer count, spending limit, and service fees. You can also filter this data using the date filter.`,
    quickReplies: ["Customers section", "Schedule section", "Date filter help"]
  },

  // Customers Section
  {
    keywords: [
      "what is the customers section", "customers section", "rider list", "visits count",
      "what is the customers section?", "average sale", "customer analytics", "visitor list",
      "customer details", "customer history", "visitor metrics"
    ],
    answer: `The **Customers section** displays all riders who visited your place, including date, time, name, visits, rating, and average sale. You can use date filters for custom periods.`,
    quickReplies: ["Ratings section", "Dashboard", "Date filter help"]
  },

  // Schedule Section
  {
    keywords: [
      "what is the schedule section", "schedule section", "operating hours",
      "what is the schedule section?", "weekly check-in", "weekly sales", "avg spend",
      "schedule management", "business hours", "hours settings"
    ],
    answer: `**Schedule** allows you to manage your operating hours, average weekly check-ins, average weekly sales, average per spend, and service fee details.`,
    quickReplies: ["Business Hours", "Happy Hours", "Holiday Hours"]
  },

  // Menu Section
  {
    keywords: [
      "what is the menu section", "menu section", "categories", "cuisines",
      "what is the menu section?", "add item", "edit price", "menu management",
      "products", "services", "pricing", "menu items"
    ],
    answer: `**Menu** allows you to manage items or services offered at your place. You can add, edit, or remove items, update prices, and manage promotions. Menu contains two sections: **Categories** (Featured items, Happy hour items) and **Cuisines** (all listed items).`,
    quickReplies: ["Featured Items", "Happy Hour Items", "Update prices"]
  },

  // Featured Items
  {
    keywords: [
      "what are featured items", "featured items", "special products",
      "what are featured items?", "highlighted items", "featured items placement",
      "promote items", "top items", "bestsellers", "showcase items"
    ],
    answer: `**Featured Items** are special highlighted products or services showcased to get more customer attention and boost sales.`,
    quickReplies: ["Menu section", "Happy Hour Items", "Update prices"]
  },

  // Happy Hour Items
  {
    keywords: [
      "what are happy hour items", "happy hour items", "discounted price",
      "what are happy hour items?", "off-peak hours", "discount items", "special offers",
      "happy hour deals", "time limited offers"
    ],
    answer: `**Happy Hour Items** are products/services offered at a discount during specific time periods to increase sales during off-peak hours.`,
    quickReplies: ["Menu section", "Set Happy Hours", "Happy Hours"]
  },

  // Business Hours
  {
    keywords: [
      "what are business hours", "business hours", "opening hours", "closing hours",
      "what are business hours?", "set hours", "operating hours", "work hours",
      "business timing", "opening time", "closing time"
    ],
    answer: `**Business Hours** allows you to set and update opening and closing times. You can also add special hours for holidays or events.`,
    quickReplies: ["Happy Hours", "Holiday Hours", "Pause my place"]
  },

  // Happy Hours Places
  {
    keywords: [
      "what are happy hours", "happy hours", "discount time", "increase sales off-peak",
      "what are happy hours?", "special hours", "discounted period", "happy hour time",
      "set happy hours", "when is happy hour"
    ],
    answer: `**Happy Hours** are special time slots where selected items are offered at discounted prices to attract more customers.`,
    quickReplies: ["Business Hours", "Happy Hour Items", "Set Hours"]
  },

  // Holiday Hours
  {
    keywords: [
      "what are holiday hours", "holiday hours", "special operating hours",
      "what are holiday hours?", "open on holiday", "holiday schedule", "holiday timing",
      "festival hours", "event hours"
    ],
    answer: `**Holiday Hours** are special operating hours for holidays. These may differ from regular hours and inform customers of availability.`,
    quickReplies: ["Pause place", "Business Hours", "Set Hours"]
  },

  // Pause Place
  {
    keywords: [
      "pause my place", "how can i pause my place temporarily",
      "how can I pause my place temporarily?", "temporarily pause", "inactive place",
      "pause business", "closed temporarily", "holiday pause", "pause operations"
    ],
    answer: `Go to **Holiday Hours** inside Business Hours and select **Pause**. Your place will be inactive for the selected period and customers cannot place orders.`,
    quickReplies: ["Holiday Hours", "Business Hours", "Resume place"]
  },

  // Payment Section Places
  {
    keywords: [
      "what is the payment section", "payment section places", "add payment method",
      "what is the payment section?", "coupons", "redeem coupon", "payment management",
      "coupon codes", "add coupons"
    ],
    answer: `**Payment section** lets the owner add payment methods and manage coupons. Owners can enter coupon codes to redeem claims.`,
    quickReplies: ["Invoices", "Add coupon code", "Payment setup"]
  },

  // Invoices Places
  {
    keywords: [
      "what are invoices", "invoices", "billing details", "order number",
      "what are invoices?", "receipt", "payment invoice", "download invoice",
      "invoice history", "transaction details"
    ],
    answer: `**Invoices** let you view and download billing details for rides, deliveries, and payments. Each invoice shows order number, date, payment ID, status, description, amount, and receipt.`,
    quickReplies: ["Download invoice", "Payment section places"]
  },

  // Berymo Code
  {
    keywords: [
      "what is the berymo code", "berymo code", "qr code", "promo code",
      "what is the berymo code?", "check-in code", "add waiter", "qr scanning",
      "unique code", "code generation"
    ],
    answer: `**Berymo Code** provides a unique QR or promo code for the place. Riders scan this code for Check-Ins. You can also add new waiters from this section.`,
    quickReplies: ["How Check-In works", "Add staff", "QR code issues"]
  },

  // Ratings Section Places
  {
    keywords: [
      "what is the ratings section", "ratings section", "rider feedback",
      "what is the ratings section?", "average ratings", "filter by tab", "review section",
      "customer feedback", "rating details", "reviews"
    ],
    answer: `**Ratings** show feedback from riders. You can view average ratings, comments, and improve customer experience. Filters allow sorting by date ranges.`,
    quickReplies: ["Customers section", "Date range filter", "Improve ratings"]
  },

  // Help Section Places
  {
    keywords: [
      "what is the help section", "places help", "support options", "guides",
      "what is the help section?", "faqs", "help center", "support resources"
    ],
    answer: `**Help** provides guides, FAQs, and direct support options for issues related to your dashboard or rides.`,
    quickReplies: ["Create support ticket", "View my tickets", "FAQ"]
  },

  // Create Support Ticket
  {
    keywords: [
      "create support ticket", "how can i create a support ticket",
      "how can I create a support ticket?", "contact support places", "submit ticket",
      "file support ticket", "new ticket", "open ticket"
    ],
    answer: `Go to the **Help section**, click **Create Ticket**, fill in your issue details, and submit. Support will respond with updates.`,
    quickReplies: ["View my tickets", "Contact support", "Help section"]
  },

  // View My Tickets
  {
    keywords: [
      "view my tickets", "how can i view my support tickets",
      "how can I view my support tickets?", "my tickets places", "ticket status",
      "support ticket history", "track ticket", "ticket updates"
    ],
    answer: `Go to **Help → My Tickets** to view all submitted tickets, statuses, and responses.`,
    quickReplies: ["Create new ticket", "Support history", "Help section"]
  },

  // Sign Out Places
  {
    keywords: [
      "sign out places", "how to sign out from the places dashboard",
      "how to sign out from the places dashboard?", "logout dashboard", "log out places",
      "sign off places", "exit dashboard"
    ],
    answer: `Go to the **Profile section** and click **Sign Out** to securely log out of your account.`,
    quickReplies: ["Sign-in help", "Profile settings", "Create Account Places"]
  },

  // ==================================================================
  // 4. GENERAL HELP & FALLBACK
  // ==================================================================

  {
    keywords: [
      "help", "support", "assistance", "contact us", "customer service",
      "get help", "need help", "have a question", "i have a problem"
    ],
    answer: `I'm here to help! I can assist you with questions about rides, payments, Check-Ins, driver operations, and restaurant management. What would you like help with?`,
    quickReplies: ["Book a ride", "I'm a driver", "Restaurant partner", "Payment help"]
  },

];

export default berymoFaqData;
